import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../api.js';
import { useMe } from '../auth.js';
import { Button, ErrorText, Input } from '../components/ui.js';

export interface Partner {
  id: string;
  name: string;
  legalName: string | null;
  address: string | null;
  vatId: string | null;
  contactEmails: string[];
  statementLanguage: 'en' | 'de';
  invoiceTaxMode: InvoiceTaxMode;
  isHouse: boolean;
}

export type InvoiceTaxMode = 'de_standard' | 'kleinunternehmer' | 'reverse_charge';
export const TAX_MODE_TEXT: Record<InvoiceTaxMode, string> = {
  de_standard: 'German VAT 19 % on top of the payout',
  kleinunternehmer: 'Kleinunternehmer (section 19 UStG), no VAT',
  reverse_charge: 'Reverse charge (EU/foreign, section 13b UStG), no VAT',
};

type Draft = { name: string; legalName: string; address: string; vatId: string; contactEmails: string; statementLanguage: 'en' | 'de'; invoiceTaxMode: InvoiceTaxMode };
const empty: Draft = { name: '', legalName: '', address: '', vatId: '', contactEmails: '', statementLanguage: 'en', invoiceTaxMode: 'de_standard' };
const toDraft = (p: Partner): Draft => ({
  name: p.name,
  legalName: p.legalName ?? '',
  address: p.address ?? '',
  vatId: p.vatId ?? '',
  contactEmails: p.contactEmails.join(', '),
  statementLanguage: p.statementLanguage,
  invoiceTaxMode: p.invoiceTaxMode,
});
const toBody = (d: Draft) => ({
  name: d.name,
  legalName: d.legalName || null,
  address: d.address || null,
  vatId: d.vatId || null,
  contactEmails: d.contactEmails.split(/[,\s]+/).filter(Boolean),
  statementLanguage: d.statementLanguage,
  invoiceTaxMode: d.invoiceTaxMode,
});
const errorText = (e: unknown) =>
  e instanceof ApiError ? ({ name_taken: 'A partner with this name exists.', partner_in_use: 'The partner has shares or statements and cannot be deleted.', house_partner: 'SilentFuture cannot be deleted.' } as Record<string, string>)[e.code] ?? e.message : 'Request failed';

export function PartnersPage() {
  const isAdmin = useMe().user?.role === 'admin';
  const client = useQueryClient();
  const partners = useQuery({ queryKey: ['partners'], queryFn: () => api<Partner[]>('GET', '/api/partners') });
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<Draft>(empty);
  const save = useMutation({
    mutationFn: () => (editing === 'new' ? api('POST', '/api/partners', toBody(draft)) : api('PATCH', `/api/partners/${editing}`, toBody(draft))),
    onSuccess: async () => {
      setEditing(null);
      await client.invalidateQueries({ queryKey: ['partners'] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api('DELETE', `/api/partners/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['partners'] }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  const field = (key: keyof Draft, label: string) => (
    <Input label={label} value={draft[key]} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} required={key === 'name'} />
  );

  return (
    <section className="space-y-6">
      <div className="flex items-center gap-4">
        <h1 className="text-xl font-semibold">Partners</h1>
        {isAdmin && (
          <Button onClick={() => { setDraft(empty); setEditing('new'); }}>New partner</Button>
        )}
      </div>
      {remove.isError && <ErrorText>{errorText(remove.error)}</ErrorText>}
      <table className="w-full text-left text-sm">
        <thead className="text-slate-500">
          <tr><th className="py-1">Name</th><th>Legal name</th><th>VAT ID</th><th>Contacts</th><th>Language</th><th>Invoice VAT</th>{isAdmin && <th />}</tr>
        </thead>
        <tbody>
          {partners.data?.map((p) => (
            <tr key={p.id} className="border-t border-slate-200 dark:border-slate-800">
              <td className="py-1.5">{p.name}{p.isHouse && <span className="ml-2 rounded bg-slate-200 px-1 text-xs dark:bg-slate-800">house</span>}</td>
              <td>{p.legalName}</td>
              <td>{p.vatId}</td>
              <td>{p.contactEmails.join(', ')}</td>
              <td>{p.statementLanguage}</td>
              <td>{p.isHouse ? '-' : TAX_MODE_TEXT[p.invoiceTaxMode]}</td>
              {isAdmin && (
                <td className="space-x-2 text-right">
                  <button className="underline" onClick={() => { setDraft(toDraft(p)); setEditing(p.id); }}>edit</button>
                  {!p.isHouse && (
                    <button className="underline text-red-700 dark:text-red-400" onClick={() => window.confirm(`Delete ${p.name}?`) && remove.mutate(p.id)}>delete</button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {editing && (
        <form onSubmit={submit} className="grid max-w-2xl grid-cols-2 gap-3 rounded border border-slate-200 p-4 dark:border-slate-800">
          <h2 className="col-span-2 font-semibold">{editing === 'new' ? 'New partner' : `Edit ${draft.name}`}</h2>
          {field('name', 'Name')}
          {field('legalName', 'Legal name')}
          {field('vatId', 'VAT ID')}
          {field('contactEmails', 'Contact emails (comma separated)')}
          <label className="col-span-2 block text-sm">
            <span className="mb-1 block text-slate-600 dark:text-slate-400">Address</span>
            <textarea className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 dark:border-slate-700 dark:bg-slate-900" rows={3} value={draft.address} onChange={(e) => setDraft({ ...draft, address: e.target.value })} />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-slate-600 dark:text-slate-400">Statement language</span>
            <select className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 dark:border-slate-700 dark:bg-slate-900" value={draft.statementLanguage} onChange={(e) => setDraft({ ...draft, statementLanguage: e.target.value as 'en' | 'de' })}>
              <option value="en">English</option>
              <option value="de">Deutsch</option>
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-slate-600 dark:text-slate-400">Invoice VAT (expected invoice total and Lexware tax)</span>
            <select aria-label="Invoice VAT" className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 dark:border-slate-700 dark:bg-slate-900" value={draft.invoiceTaxMode} onChange={(e) => setDraft({ ...draft, invoiceTaxMode: e.target.value as InvoiceTaxMode })}>
              {(Object.keys(TAX_MODE_TEXT) as InvoiceTaxMode[]).map((m) => <option key={m} value={m}>{TAX_MODE_TEXT[m]}</option>)}
            </select>
          </label>
          {save.isError && <div className="col-span-2"><ErrorText>{errorText(save.error)}</ErrorText></div>}
          <div className="col-span-2 space-x-2">
            <Button type="submit" disabled={save.isPending}>Save</Button>
            <button type="button" className="text-sm underline" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </form>
      )}
    </section>
  );
}
